<script setup lang="ts">
    import { ref, reactive, watch, inject, Ref, onMounted } from 'vue';
    import { useRoute } from 'vue-router';
    import moment from 'moment';
    import axios from 'axios';
    import { useToastStore } from '@/stores/toast.store';

    const route = useRoute();
    const toastStore = useToastStore();
    const errorMessage = ref('');
    const loading = ref(false);
    const collectivite = inject<Ref<any>>('collectivite');
    const startupFormDatas = reactive({
        pays: [] as any[],
        users: [] as any[]
    });
    const gerant = ref(null as any);
    const membres = ref([] as any[]);
    const gerantType = ref<'new' | 'existing'>('new'); // Type de gérant : 'new' ou 'existing'
    const isFormValid = ref(false);
    const formData = reactive({
        nom: null as string | null,
        prenom: null as string | null,
        date_naissance: null as string | null,
        age: 0,
        telephone: null as string | null,
        email: null as string | null,
        gerant_id: null as number | null // ID de l'utilisateur existant à associer
    });
    const errors = reactive({
        nom: "",
        prenom: "",
        date_naissance: "",
        age: "",
        telephone: "",
        email: "",
        gerant_id: ""
    });

    function recomputeAge() {
        if (!formData.date_naissance) { formData.age = 0; return; }
        const birthDate = moment(formData.date_naissance);
        if (birthDate.isValid()) formData.age = moment().diff(birthDate, 'years');
    }

    function onDateNaissanceChange(e: Event) {
        formData.date_naissance = (e.target as HTMLInputElement).value;
        recomputeAge();
    }

    // Fallback : watcher pour binding programmatique
    watch(() => formData.date_naissance, () => recomputeAge());

    onMounted(() => {
        const dbMembres = (route.meta.item as any).membres as any[];
        if (dbMembres) {
            membres.value = dbMembres;
        }
        const dbGerant = (route.meta.item as any).gerant as any;
        if (dbGerant) {
            _setGerant(dbGerant);
        }
    })

    function _setGerant(dbGerant: any) {
        gerant.value = dbGerant;
        Object.assign(formData, {
            nom: dbGerant.nom,
            prenom: dbGerant.prenom,
            date_naissance: moment(dbGerant.date_naissance).format('YYYY-MM-DD'),
            age: dbGerant.age,
            telephone: dbGerant.telephone,
            email: dbGerant.email,
            gerant_id: dbGerant.gerant_id
        });
        gerantType.value = 'new';
    }

    function validate() {
        let isValid = true;
        errors.gerant_id = '';
        errors.nom = '';
        errors.prenom = '';
        errors.date_naissance = '';
        errors.age = '';
        errors.telephone = '';
        errors.email = '';

        if (gerantType.value === 'existing') {
            // Si on sélectionne un utilisateur existant, vérifier que gerant_id est défini
            if (!formData.gerant_id) {
                errors.gerant_id = "Veuillez sélectionner un utilisateur existant";
                isValid = false;
            }
        } else {
            errors.nom = '';
            if (!formData.nom) {
                errors.nom ="Le nom est requis";
                isValid = false;
            }

            errors.prenom = '';
            if (!formData.prenom) {
                errors.prenom ="Le prénom est requis";
                isValid = false;
            }

            errors.date_naissance = '';
            if (!formData.date_naissance) {
                errors.date_naissance ="La date de naissance est requise";
                isValid = false;
            } else {
                const birthDate = moment(formData.date_naissance);
                if (!birthDate.isValid()) {
                    errors.date_naissance ="Le format de la date de naissance est invalide";
                    isValid = false;
                }
            }

            errors.age = '';
            if (!formData.age) {
                errors.age ="L'âge est requis";
                isValid = false;
            } else if (isNaN(formData.age) || formData.age <= 0) {
                errors.age ="Le format de l'âge est invalide";
                isValid = false;
            }

            errors.telephone = '';
            if (!formData.telephone) {
                errors.telephone ="Le téléphone est requis";
                isValid = false;
            }

            errors.email = '';
            if (!formData.email) {
                errors.email ="L'email est requis";
                isValid = false;
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                errors.email = "Le format de l'email est invalide";
                isValid = false;
            }
        }

        return isValid;
    }

    async function onSubmit() {
        if (!validate()) { return; }
        loading.value = true;
        errorMessage.value = '';
        try {
            const formToUpload = {
                gerantType: gerantType.value,
                nom: formData.nom,
                prenom: formData.prenom,
                date_naissance: formData.date_naissance,
                age: formData.age,
                telephone: formData.telephone,
                email: formData.email,
                gerant_id: formData.gerant_id,
            };

            const response = await axios.post(`/backoffice/collectivites/${collectivite!.value.id}/update_gerant`, formToUpload);
            if (response.data.gerant) {
                _setGerant(response.data.gerant);
            }
            toastStore.success("Modifications effectuées");
        } catch(error: any) {
            console.error(error);
            toastStore.error(error.message || 'Une erreur est survenue lors de la requête au serveur.');
        } finally {
            loading.value = false;
        }
    }
</script>
<template>
    <section class="s20_mobile px-4">
        <div class="widget px-7 py-7">
            
            <div class="s10_mobile container-full-2"> 
                <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                    <img src="/public/icons/icon-info-perso.svg" class="size-l" alt="" />
                    Gérant
                </h2>
            </div> 
            <div class="clear-both"></div>

            <!-- Choix du type de gérant -->
            <div class="s20_mobile container-full-1 my-5" v-if="!gerant">
                <label class="font-medium mb-3 block">Type de gérant <b class="color-danger">*</b></label>
                <div class="grid2 grid-row-gap-0">
                    <div class="card-check lg single">
                        <label class="form-check-label" for="gerant_type_new">
                            <div class="card-check-text">
                                <input
                                    class="form-check-input left"
                                    type="radio"
                                    name="gerant_type"
                                    id="gerant_type_new"
                                    value="new"
                                    v-model="gerantType"
                                />
                                <h6 class="font-semibold mt-1 mb-2 align-right">Créer un nouveau gérant</h6>
                            </div>
                        </label>
                    </div>
                    <div class="card-check lg single">
                        <label class="form-check-label" for="gerant_type_existing">
                            <div class="card-check-text">
                                <input
                                    class="form-check-input left"
                                    type="radio"
                                    name="gerant_type"
                                    id="gerant_type_existing"
                                    value="existing"
                                    v-model="gerantType"
                                />
                                <h6 class="font-semibold mt-1 mb-2 align-right">Utiliser un utilisateur existant</h6>
                            </div>
                        </label>
                    </div>
                </div>
            </div>

            <!-- Sélection d'un utilisateur existant -->
            <div v-if="gerantType === 'existing'">
                <div class="form-field-floating active my-5">
                    <select class="form-input-select" name="gerant_id" id="gerant_id" v-model="formData.gerant_id">
                        <option :value="null" selected>Sélectionnez un utilisateur</option>
                        <option :value="user.id" v-for="(user, index) in membres" :key="'user_' + index">
                            {{ user.fullname }} - {{ user.email }} {{ user.fonction ? '(' + user.fonction + ')' : '' }}
                        </option>
                    </select>
                    <label for="gerant_id">Utilisateur existant <b class="color-danger">*</b></label>
                </div>
                <span v-if="!!errors.gerant_id" class="text-red">{{ errors.gerant_id }}</span>
                <p v-if="startupFormDatas.users.length === 0" class="text-grey-500 text-sm mt-2">
                    Aucun utilisateur disponible. Créez d'abord un utilisateur ou choisissez "Créer un nouveau gérant".
                </p>
            </div>

            <!-- Formulaire complet pour créer un nouveau gérant -->
            <form class="s20 form" v-else>

                <div class="container-full-1">
                    <h3 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                        <img src="/icons/icon-info-perso.svg" class="size-base" alt="" />
                        Informations personnelles
                    </h3>

                    <div class="form-field-floating reactive active my-5">
                        <input type="text" class="form-input" v-model="formData.nom" name="nom" id="nom" placeholder="">
                        <label for="nom">Nom <b class="color-danger">*</b></label>
                    </div>
                    <span v-if="!!errors['nom']" class="text-red">{{ errors['nom'] }}</span>
                    
                    <div class="form-field-floating reactive my-5">
                        <input type="text" class="form-input" v-model="formData.prenom" name="prenom" id="prenom" placeholder="">
                        <label for="prenom">Prénoms <b class="color-danger">*</b></label>
                    </div>
                    <span v-if="!!errors['prenom']" class="text-red">{{ errors['prenom'] }}</span>

                    <div class="form-field-split my-5">
                        <div class="form-field-floating reactive half">
                            <input type="date" class="form-input" :value="formData.date_naissance" @input="onDateNaissanceChange" @change="onDateNaissanceChange" name="date_naissance" id="date_naissance" placeholder="">
                            <label for="date_naissance">Date de naissance <b class="color-danger">*</b></label>
                        </div>
                        <hr class="hr" />
                        <div class="form-field-floating reactive half">
                            <input type="text" class="form-input" v-model="formData.age" name="age" id="age" placeholder="" readonly>
                            <label for="age">Age <b class="color-danger">*</b></label>
                        </div>
                    </div>
                    <span v-if="!!errors['date_naissance']" class="text-red">{{ errors['date_naissance'] }}</span>
                    <span v-if="!!errors['age']" class="text-red">{{ errors['age'] }}</span>
                </div>

                <div class="clear-both h-5"></div>

                <div class="container-full-1">
                    <div class="form-field-split active my-5">
                        <custom-phone-input v-model="formData.telephone" id="telephone" classesNames="half" />
                        <hr class="hr" />
                        <div class="form-field-floating reactive half">
                            <input type="text" class="form-input" v-model="formData.email" name="email" id="email" placeholder="">
                            <label for="email">Adresse Email <b class="color-danger">*</b></label>
                        </div>
                    </div>
                    <span v-if="!!errors['email']" class="text-red">{{ errors['email'] }}</span>
                </div>

            </form>

            <!-- Actions -->
            <div class="s10_mobile container-full-2">&nbsp;</div>
            <div class="s10_mobile container-full-2">
                <span v-if="!!errorMessage" class="text-red">{{ errorMessage }}</span>
                <p class="text-xs text-grey-500 mb-5"><b class="color-danger text-xs">*</b> Les champs marqués d'un astérisque sont obligatoires</p>
                <div class="flex gap-2">
                    <custom-button type="button" :classesList="'btn lg w-100'" :loading="loading" @click="onSubmit()">
                        <img src="/icons/icon-enregistrer.svg" class="btn-icon" alt="" />
                        <span>Valider</span>
                    </custom-button>
                </div>
            </div>
            <div class="clear-both h-25"></div>
        </div>
    </section>
</template>